/**
 * UpdatesTab — read-only paginated history of all updates for a relationship.
 *
 * Two sub-tabs:
 *   - Claims:      memory_claims (digest-emitted relational state + profile_field claims)
 *   - Bio Updates: bio_claim_actions (Bio Update System review queue + history)
 *
 * Both lists support:
 *   - status dropdown filter (`all` + per-collection statuses)
 *   - text search (debounced, server-side)
 *   - 25-row pagination
 *
 * Scope: match_persona-side only (this is a match-detail page).
 * Actions: none — pure history view. Use the Brain Sheet to act on rows.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Loader2, Search, ChevronLeft, ChevronRight, Brain, Sparkles, BookOpen, Check } from "lucide-react";
import { authAxios } from "../../utils/auth";
import { toast } from "sonner";

const PAGE_SIZE = 25;

const CLAIM_STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "resolved", label: "Resolved" },
  { value: "retracted", label: "Retracted" },
  { value: "superseded", label: "Superseded" },
  { value: "decayed", label: "Decayed" },
  { value: "pending_confirmation", label: "Pending confirmation" },
];

const BIO_STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "modified", label: "Modified" },
  { value: "rejected", label: "Rejected" },
  { value: "corroborated", label: "Corroborated" },
];

const NARRATIVE_STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "modified", label: "Modified" },
  { value: "rejected", label: "Rejected" },
  { value: "superseded", label: "Superseded" },
];

// Per-field filter chips on the Narrative tab. `all` shows every facet,
// the others scope the listing to a single narrative field. Order chosen
// to match the digest prompt's per-field guidance block.
const NARRATIVE_FIELD_CHIPS = [
  { value: "all",                       label: "All fields" },
  { value: "history_together",          label: "History together" },
  { value: "their_feelings_toward_you", label: "Their feelings" },
  { value: "your_feelings_toward_them", label: "Your feelings" },
  { value: "user_match_goal",           label: "Match goal" },
];

// Reusable debounced value hook
function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function formatTimestamp(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return String(ts);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return String(ts);
  }
}

function StatusPill({ status }) {
  const palette = {
    pending: "bg-amber-500/15 text-amber-300 border-amber-400/30",
    approved: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
    modified: "bg-sky-500/15 text-sky-300 border-sky-400/30",
    rejected: "bg-rose-500/15 text-rose-300 border-rose-400/30",
    corroborated: "bg-divine-gold/15 text-divine-gold border-divine-gold/30",
    active: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
    resolved: "bg-divine-gold/15 text-divine-gold border-divine-gold/30",
    retracted: "bg-rose-500/15 text-rose-300 border-rose-400/30",
    superseded: "bg-violet-500/15 text-violet-300 border-violet-400/30",
    decayed: "bg-white/10 text-white/60 border-white/20",
    pending_confirmation: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  };
  const cls = palette[status] || "bg-white/10 text-white/60 border-white/20";
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${cls}`}
      data-testid={`updates-status-${status}`}
    >
      {String(status || "—").replace(/_/g, " ")}
    </span>
  );
}

function FilterBar({
  searchValue,
  onSearchChange,
  statusValue,
  onStatusChange,
  statusOptions,
  searchPlaceholder,
}) {
  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center mb-3">
      <div className="relative flex-1">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
        <input
          type="text"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-full bg-black/40 border border-divine-gold/30 rounded p-2 pl-8 text-white text-sm focus:outline-none focus:border-divine-gold/60"
          data-testid="updates-search-input"
        />
      </div>
      <select
        value={statusValue}
        onChange={(e) => onStatusChange(e.target.value)}
        className="bg-black/40 border border-divine-gold/30 rounded p-2 text-white text-sm focus:outline-none focus:border-divine-gold/60"
        data-testid="updates-status-filter"
      >
        {statusOptions.map((opt) => (
          <option key={opt.value} value={opt.value} className="bg-black">
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Pagination({ page, total, pageSize, onPageChange }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : page * pageSize + 1;
  const end = Math.min(total, (page + 1) * pageSize);
  return (
    <div className="flex items-center justify-between text-xs text-white/60 mt-3">
      <div data-testid="updates-page-range">
        {total === 0 ? "0 results" : `${start}–${end} of ${total}`}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={page === 0}
          onClick={() => onPageChange(Math.max(0, page - 1))}
          className="p-1 rounded hover:bg-white/5 disabled:opacity-30"
          data-testid="updates-page-prev"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span data-testid="updates-page-label">
          Page {page + 1} / {totalPages}
        </span>
        <button
          type="button"
          disabled={page + 1 >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className="p-1 rounded hover:bg-white/5 disabled:opacity-30"
          data-testid="updates-page-next"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Claims list (memory_claims)
// ---------------------------------------------------------------------------

function ClaimsList({ matchId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 350);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkTarget, setBulkTarget] = useState("resolved");
  const [applyingBulk, setApplyingBulk] = useState(false);

  // Reset selection when page/filters change
  useEffect(() => { setSelectedIds(new Set()); }, [page, debouncedSearch, status]);

  // Reset to page 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, status]);

  const fetchRows = useCallback(async () => {
    if (!matchId) return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      };
      if (status !== "all") params.status = status;
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      const res = await authAxios.get(`/familiarity-v2/${matchId}/claims`, { params });
      setRows(res.data?.claims || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      console.error("ClaimsList fetch failed:", err);
      setError(err?.response?.data?.detail || "Failed to load claims.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [matchId, page, status, debouncedSearch]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const allSelected = rows.length > 0 && rows.every(r => selectedIds.has(r.claim_id || r._id));
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(rows.map(r => r.claim_id || r._id)));
  };
  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const applyBulk = async () => {
    if (selectedIds.size === 0) { toast.error("No rows selected"); return; }
    setApplyingBulk(true);
    try {
      const res = await authAxios.post(
        `/familiarity-v2/${matchId}/claims/bulk-status`,
        { claim_ids: Array.from(selectedIds), status: bulkTarget },
      );
      toast.success(`Updated ${res.data?.updated ?? 0} claim(s) → ${bulkTarget}`);
      setSelectedIds(new Set());
      await fetchRows();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk update failed");
    } finally {
      setApplyingBulk(false);
    }
  };

  return (
    <div data-testid="updates-claims-list">
      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        statusValue={status}
        onStatusChange={setStatus}
        statusOptions={CLAIM_STATUS_OPTIONS}
        searchPlaceholder="Search claim text, topic, domain…"
      />
      {/* Bulk action bar — visible whenever ≥ 1 row is selected */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-2 mb-2 p-2 rounded border border-divine-gold/30 bg-divine-gold/5" data-testid="claims-bulk-bar">
          <span className="text-xs text-white/70">{selectedIds.size} selected</span>
          <span className="text-white/30">→</span>
          <select
            value={bulkTarget}
            onChange={(e) => setBulkTarget(e.target.value)}
            className="bg-black/40 border border-divine-gold/30 rounded p-1 text-white text-xs"
            data-testid="claims-bulk-target"
          >
            <option value="active">Active</option>
            <option value="resolved">Resolved</option>
          </select>
          <button
            onClick={applyBulk}
            disabled={applyingBulk}
            className="text-xs px-3 py-1 rounded bg-divine-gold/20 hover:bg-divine-gold/30 text-divine-gold border border-divine-gold/40 disabled:opacity-50 flex items-center gap-1"
            data-testid="claims-bulk-apply"
          >
            <Check className="w-3 h-3" /> {applyingBulk ? "Applying…" : "Apply"}
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-xs px-2 py-1 text-white/50 hover:text-white"
            data-testid="claims-bulk-clear"
          >
            Clear
          </button>
        </div>
      )}
      {error && (
        <div
          className="rounded border border-rose-500/30 bg-rose-500/10 p-2 text-xs text-rose-200 mb-2"
          data-testid="updates-claims-error"
        >
          {error}
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-white/50" data-testid="updates-claims-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading claims…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-divine-gold/15 bg-divine-black/30 p-6 text-center space-y-2" data-testid="updates-claims-empty">
          <Brain className="w-5 h-5 mx-auto text-divine-gold/60" />
          <div className="text-sm text-white/70">No claims match your filters.</div>
          <div className="text-xs text-white/40">
            Try widening the status filter or clearing the search.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded border border-divine-gold/15">
          <table className="w-full text-sm">
            <thead className="bg-divine-black/40 text-[11px] uppercase tracking-wider text-white/50">
              <tr>
                <th className="w-8 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="cursor-pointer accent-divine-gold"
                    data-testid="claims-select-all"
                    aria-label="Select all rows on page"
                  />
                </th>
                <th className="text-left px-3 py-2 font-medium">Date</th>
                <th className="text-left px-3 py-2 font-medium">Domain</th>
                <th className="text-left px-3 py-2 font-medium">Topic</th>
                <th className="text-left px-3 py-2 font-medium">Claim</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Conf.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map((r) => {
                const id = r.claim_id || r._id;
                const checked = selectedIds.has(id);
                return (
                  <tr key={id} className={`hover:bg-white/[0.02] ${checked ? "bg-divine-gold/[0.04]" : ""}`}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleOne(id)}
                        className="cursor-pointer accent-divine-gold"
                        data-testid={`claims-select-${id}`}
                        aria-label="Select row"
                      />
                    </td>
                    <td className="px-3 py-2 text-white/60 whitespace-nowrap">{formatTimestamp(r.created_at)}</td>
                    <td className="px-3 py-2 text-white/80 whitespace-nowrap capitalize">{(r.domain || "").replace(/_/g, " ")}</td>
                    <td className="px-3 py-2 text-white/80 whitespace-nowrap">{r.topic || "—"}</td>
                    <td className="px-3 py-2 text-white/90 max-w-md break-words">{r.claim_text || "—"}</td>
                    <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                    <td className="px-3 py-2 text-white/60 whitespace-nowrap">
                      {typeof r.confidence === "number" ? r.confidence.toFixed(2) : (r.confidence || "—")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={page} total={total} pageSize={PAGE_SIZE} onPageChange={setPage} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bio Updates list (bio_claim_actions)
// ---------------------------------------------------------------------------

function BioUpdatesList({ matchId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 350);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, status]);

  const fetchRows = useCallback(async () => {
    if (!matchId) return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        // `target_entity_id` is unique across both user_persona and
        // match_persona buckets, so we don't need to pin `target_entity`
        // here. Pinning it would break this page when matchId resolves to
        // a user_persona (e.g. the owning user's own self-persona).
        target_entity_id: matchId,
        status, // already supports "all" server-side
      };
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      const res = await authAxios.get(`/bio-claims`, { params });
      setRows(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      console.error("BioUpdatesList fetch failed:", err);
      setError(err?.response?.data?.detail || "Failed to load bio updates.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [matchId, page, status, debouncedSearch]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const formatValueCell = (v) => {
    if (v == null) return "—";
    if (Array.isArray(v)) return v.join(", ");
    if (typeof v === "boolean") return v ? "Yes" : "No";
    return String(v);
  };

  return (
    <div data-testid="updates-bio-list">
      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        statusValue={status}
        onStatusChange={setStatus}
        statusOptions={BIO_STATUS_OPTIONS}
        searchPlaceholder="Search field, proposed value, evidence…"
      />
      {error && (
        <div
          className="rounded border border-rose-500/30 bg-rose-500/10 p-2 text-xs text-rose-200 mb-2"
          data-testid="updates-bio-error"
        >
          {error}
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-white/50" data-testid="updates-bio-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading bio updates…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-divine-gold/15 bg-divine-black/30 p-6 text-center space-y-2" data-testid="updates-bio-empty">
          <Sparkles className="w-5 h-5 mx-auto text-divine-gold/60" />
          <div className="text-sm text-white/70">No bio updates match your filters.</div>
          <div className="text-xs text-white/40">
            Try widening the status filter or clearing the search.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded border border-divine-gold/15">
          <table className="w-full text-sm">
            <thead className="bg-divine-black/40 text-[11px] uppercase tracking-wider text-white/50">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Date</th>
                <th className="text-left px-3 py-2 font-medium">Field</th>
                <th className="text-left px-3 py-2 font-medium">Action</th>
                <th className="text-left px-3 py-2 font-medium">Proposed value</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Conf.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map((r) => (
                <tr key={r.claim_id} className="hover:bg-white/[0.02]">
                  <td className="px-3 py-2 text-white/60 whitespace-nowrap">{formatTimestamp(r.created_at)}</td>
                  <td className="px-3 py-2 text-divine-gold whitespace-nowrap">
                    {(r.field_name || "").replace(/_/g, " ")}
                    {r.field_tier ? (
                      <span className="ml-1 text-[10px] text-white/40">T{r.field_tier}</span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-white/70 whitespace-nowrap capitalize">{r.action || "—"}</td>
                  <td className="px-3 py-2 text-white/90 max-w-md break-words">{formatValueCell(r.proposed_value)}</td>
                  <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                  <td className="px-3 py-2 text-white/60 whitespace-nowrap">{r.confidence || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={page} total={total} pageSize={PAGE_SIZE} onPageChange={setPage} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Narrative list (narrative_synthesis_actions)
// ---------------------------------------------------------------------------

function NarrativeList({ matchId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 350);
  const [status, setStatus] = useState("all");
  const [fieldChip, setFieldChip] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Filter handlers reset pagination inline (avoids set-state-in-effect).
  const handleSearchChange = useCallback((next) => {
    setSearch(next);
    setPage(0);
  }, []);
  const handleStatusChange = useCallback((next) => {
    setStatus(next);
    setPage(0);
  }, []);
  const handleFieldChipChange = useCallback((next) => {
    setFieldChip(next);
    setPage(0);
  }, []);

  const fetchRows = useCallback(async () => {
    if (!matchId) return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        target_entity_id: matchId,
        status,
      };
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      const res = await authAxios.get(`/bio-claims/narrative`, { params });
      let items = res.data?.items || [];
      // Field-chip filter is applied client-side; the BE endpoint doesn't
      // expose a field_name query param yet, and page sizes are small.
      const totalRaw = res.data?.total || 0;
      if (fieldChip !== "all") {
        items = items.filter((r) => r.field_name === fieldChip);
      }
      setRows(items);
      // When the chip narrows results, the BE total is an over-count.
      // Show the BE total when chip == all (true server total) and the
      // page-local filtered count otherwise (best the FE can do without
      // a server-side field filter).
      setTotal(fieldChip === "all" ? totalRaw : items.length);
    } catch (err) {
      console.error("NarrativeList fetch failed:", err);
      setError(err?.response?.data?.detail || "Failed to load narrative history.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [matchId, page, status, debouncedSearch]);

  // Standard fetch-on-callback-change pattern (matches ClaimsList +
  // BioUpdatesList above). The `react-hooks/set-state-in-effect` rule
  // flags this pattern across all three list components; the alternative
  // (top-level fetch) would double-fetch on mount under React StrictMode
  // in dev. Tracked in the ESLint cleanup item on the P2 backlog.
  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const renderPreview = (text, max = 140) => {
    if (!text) return <span className="text-white/40 italic">— empty —</span>;
    const t = String(text);
    if (t.length <= max) return t;
    return t.slice(0, max).trimEnd() + "…";
  };

  return (
    <div data-testid="updates-narrative-list">
      <FilterBar
        searchValue={search}
        onSearchChange={handleSearchChange}
        statusValue={status}
        onStatusChange={handleStatusChange}
        statusOptions={NARRATIVE_STATUS_OPTIONS}
        searchPlaceholder="Search field, synthesised text, evidence…"
      />
      <div
        className="flex flex-wrap gap-1.5 mb-3"
        data-testid="updates-narrative-field-chips"
        role="tablist"
        aria-label="Narrative field filter"
      >
        {NARRATIVE_FIELD_CHIPS.map((chip) => {
          const active = fieldChip === chip.value;
          return (
            <button
              key={chip.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => handleFieldChipChange(chip.value)}
              className={
                "px-2.5 py-1 text-[11px] rounded-full border transition-colors " +
                (active
                  ? "bg-divine-gold/20 text-divine-gold border-divine-gold/50"
                  : "bg-white/[0.03] text-white/60 border-white/10 hover:text-white/85 hover:border-white/20")
              }
              data-testid={`updates-narrative-field-chip-${chip.value}`}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
      {error && (
        <div
          className="rounded border border-rose-500/30 bg-rose-500/10 p-2 text-xs text-rose-200 mb-2"
          data-testid="updates-narrative-error"
        >
          {error}
        </div>
      )}
      {loading ? (
        <div
          className="flex items-center justify-center py-12 text-white/50"
          data-testid="updates-narrative-loading"
        >
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading narrative history…
        </div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-md border border-divine-gold/15 bg-divine-black/30 p-6 text-center space-y-2"
          data-testid="updates-narrative-empty"
        >
          <BookOpen className="w-5 h-5 mx-auto text-divine-gold/60" />
          <div className="text-sm text-white/70">
            No narrative syntheses match your filters.
          </div>
          <div className="text-xs text-white/40">
            As coaching sessions add material new content, Eros will propose
            merged narrative updates here. Try widening the status filter.
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded border border-divine-gold/15">
          <table className="w-full text-sm">
            <thead className="bg-divine-black/40 text-[11px] uppercase tracking-wider text-white/50">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Date</th>
                <th className="text-left px-3 py-2 font-medium">Field</th>
                <th className="text-left px-3 py-2 font-medium">Proposed</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Conf.</th>
                <th className="text-left px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {rows.map((r) => {
                const isOpen = expandedId === r.synthesis_id;
                const committed =
                  r.status === "approved" || r.status === "modified"
                    ? r.final_value || r.synthesised_value
                    : r.synthesised_value;
                return (
                  <React.Fragment key={r.synthesis_id}>
                    <tr
                      className="hover:bg-white/[0.02] cursor-pointer"
                      onClick={() =>
                        setExpandedId(isOpen ? null : r.synthesis_id)
                      }
                      data-testid={`updates-narrative-row-${r.synthesis_id}`}
                    >
                      <td className="px-3 py-2 text-white/60 whitespace-nowrap">
                        {formatTimestamp(r.created_at)}
                      </td>
                      <td className="px-3 py-2 text-divine-gold whitespace-nowrap">
                        {(r.field_name || "").replace(/_/g, " ")}
                      </td>
                      <td className="px-3 py-2 text-white/90 max-w-md break-words">
                        {renderPreview(committed)}
                      </td>
                      <td className="px-3 py-2">
                        <StatusPill status={r.status} />
                      </td>
                      <td className="px-3 py-2 text-white/60 whitespace-nowrap">
                        {r.confidence || "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-[11px] text-white/40">
                        {isOpen ? "hide" : "view"}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr
                        className="bg-divine-black/40"
                        data-testid={`updates-narrative-row-expanded-${r.synthesis_id}`}
                      >
                        <td colSpan={6} className="px-3 py-3 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="rounded border border-white/10 bg-white/5 p-2">
                              <div className="text-[10px] uppercase text-white/40 mb-1">
                                Prior value (at emission)
                              </div>
                              <div className="text-white/70 whitespace-pre-wrap leading-relaxed text-[13px]">
                                {r.prior_value
                                  ? r.prior_value
                                  : <span className="italic text-white/40">— empty —</span>}
                              </div>
                            </div>
                            <div className="rounded border border-divine-gold/30 bg-divine-gold/5 p-2">
                              <div className="text-[10px] uppercase text-divine-gold/60 mb-1">
                                {r.status === "modified"
                                  ? "Final value (user-edited)"
                                  : r.status === "approved"
                                  ? "Approved value"
                                  : "Proposed value"}
                              </div>
                              <div className="text-divine-gold whitespace-pre-wrap leading-relaxed text-[13px]">
                                {committed}
                              </div>
                            </div>
                          </div>
                          {r.evidence_summary && (
                            <div className="text-[12px] text-white/60 italic px-1">
                              <span className="text-white/40 not-italic">
                                What changed:
                              </span>{" "}
                              {r.evidence_summary}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-white/40">
                            <div>
                              session_id:{" "}
                              <span className="text-white/60">
                                {r.session_id || "—"}
                              </span>
                            </div>
                            <div>
                              synthesis_id:{" "}
                              <span className="text-white/60">
                                {r.synthesis_id}
                              </span>
                            </div>
                            {r.reviewed_at && (
                              <div>
                                reviewed:{" "}
                                <span className="text-white/60">
                                  {formatTimestamp(r.reviewed_at)}
                                </span>
                              </div>
                            )}
                            {r.superseded_by && (
                              <div>
                                superseded by:{" "}
                                <span className="text-white/60">
                                  {r.superseded_by}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={page} total={total} pageSize={PAGE_SIZE} onPageChange={setPage} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main UpdatesTab
// ---------------------------------------------------------------------------

export default function UpdatesTab({ matchId }) {
  const [subTab, setSubTab] = useState("claims");
  return (
    <div className="space-y-3" data-testid="updates-tab-root">
      <div
        className="rounded-md border border-divine-gold/20 bg-divine-black/40 p-3 text-xs text-white/70 leading-relaxed"
        data-testid="updates-tab-intro"
      >
        <p className="text-white/85">
          Automated updates Agent Eros has gathered from your coaching sessions.
        </p>
        <p className="mt-1.5 text-white/60">
          <span className="text-divine-gold">Claims</span> are individual facts
          extracted from your conversations (their preferences, habits, past
          events). <span className="text-divine-gold">Bio Updates</span> are
          proposed edits to their profile fields — things like relationship
          status, occupation, or family details.{" "}
          <span className="text-divine-gold">Narrative</span> are AI-synthesised
          summaries of the story-fields (history together, feelings, goals).
        </p>
        <p className="mt-1.5 text-white/50 italic">
          Everything here is derived from what you tell Eros — nothing is
          published to their profile until you approve it in the Brain Sheet.
        </p>
      </div>
      <Tabs value={subTab} onValueChange={setSubTab} className="w-full">
        <TabsList className="bg-divine-black/40 border border-divine-gold/15">
          <TabsTrigger
            value="claims"
            className="data-[state=active]:bg-divine-gold/15 data-[state=active]:text-divine-gold text-white/70"
            data-testid="updates-subtab-claims"
          >
            Claims
          </TabsTrigger>
          <TabsTrigger
            value="bio"
            className="data-[state=active]:bg-divine-gold/15 data-[state=active]:text-divine-gold text-white/70"
            data-testid="updates-subtab-bio"
          >
            Bio Updates
          </TabsTrigger>
          <TabsTrigger
            value="narrative"
            className="data-[state=active]:bg-divine-gold/15 data-[state=active]:text-divine-gold text-white/70"
            data-testid="updates-subtab-narrative"
          >
            Narrative
          </TabsTrigger>
        </TabsList>
        <TabsContent value="claims" className="mt-3">
          <ClaimsList matchId={matchId} />
        </TabsContent>
        <TabsContent value="bio" className="mt-3">
          <BioUpdatesList matchId={matchId} />
        </TabsContent>
        <TabsContent value="narrative" className="mt-3">
          <NarrativeList matchId={matchId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
